import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchData } from "../utils/api";
import Loader from "../components/Loader.js";

const EventFacebookAdsCampaignPage = ({ companyId }) => {
  const { companySlug } = useParams();

  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopedUrl = (url) => {
    if (!companySlug) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}company_slug=${companySlug}`;
  };
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["marketing-event", "all_upcoming_events"],
    queryFn: () => fetchData("api/marketing/all_upcoming_events"),
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const eventsItems = data?.events || [];

  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const allIds = useMemo(() => eventsItems.map(e => e.id), [eventsItems]);
  const allSelected = useMemo(() => allIds.length > 0 && selectedEventIds.length === allIds.length, [allIds, selectedEventIds]);

  useEffect(() => {
    // Reset selection if events list changes
    setSelectedEventIds([]);
  }, [allIds.join(',')]);

  const toggleSelect = (id) => {
    setSelectedEventIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedEventIds(prev => (prev.length === allIds.length ? [] : allIds));
  };

  const handleGenerate = async () => {
    if (selectedEventIds.length === 0) {
      alert("Please select at least one event");
      return;
    }

    try {
      // Fetch existing Facebook ads with creative + adset details + targeting JSON.
      // include_targeting=true is required because this page renders targeting context;
      // other callers default to omitting it for performance.
      const adsResponse = await fetch(scopedUrl("/facebook_ads?include_details=true&include_targeting=true"));
      const adsData = await adsResponse.json();

      // Flatten ads (grouped by date) to a single array with creative and adset details
      const existing_ads = [];
      Object.values(adsData).forEach(dayAds => {
        dayAds.forEach(ad => {
          existing_ads.push({
            event_id: ad.event_id || null,
            message: ad.creative?.message || ad.description || "",
            headline: ad.creative?.headline || ad.title || "",
            description: ad.creative?.description || "",
            link: ad.creative?.link || "",
            media_url: ad.creative?.media_url || ad.image || "",
            scheduled_at: ad.scheduled_at || ad.published_at,
            // Ad Set details
            ad_set: ad.ad_set ? {
              name: ad.ad_set.name || "",
              budget_strategy: ad.ad_set.budget_strategy || "",
              daily_budget: ad.ad_set.daily_budget || null,
              lifetime_budget: ad.ad_set.lifetime_budget || null,
              start_date: ad.ad_set.start_date || null,
              end_date: ad.ad_set.end_date || null,
              billing_event: ad.ad_set.billing_event || "",
              optimization_goal: ad.ad_set.optimization_goal || "",
              bid_strategy: ad.ad_set.bid_strategy || "",
              targeting: ad.ad_set.targeting || {}
            } : null
          });
        });
      });

      // Helper functions for date formatting
      const toYYYYMMDD = (d) => {
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return "";
          return dt.toISOString().split('T')[0];
        } catch { return ""; }
      };
      const toISO = (d) => {
        try {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return "";
          return dt.toISOString();
        } catch { return ""; }
      };

      // Pick selected events from eventsItems and map to comprehensive structure
      const selected_events = eventsItems
        .filter(e => selectedEventIds.includes(e.id))
        .map(e => {
          // Use address object directly from API (now properly returned as JSON object)
          const addressObj = e.address || {};

          return {
            event_id: e.id,
            event_name: e.name || "",
            event_description: e.description || "",
            event_short_description: e.short_description || "",
            event_address: {
              street: addressObj.street || e.street || "",
              city: addressObj.city || e.city || "",
              state: addressObj.state || e.state || "",
              zip: addressObj.zip || e.zip || "",
              country: addressObj.country || e.country || "US",
              venue_name: e.venue_name || ""
            },
            event_market: e.market ? {
              market_id: e.market.id || e.market_id || null,
              market_name: e.market.name || e.market_name || "",
              market_cities: e.market.cities || []
            } : null,
            event_start_date: toYYYYMMDD(e.start_date),
            event_start_time: toISO(e.start_date || e.start_time),
            event_end_date: toYYYYMMDD(e.end_date),
            event_end_time: toISO(e.end_date || e.end_time),
            event_timezone: e.timezone || "",
            event_activities: e.activity_names ? e.activity_names.split(',').map(a => a.trim()) : [],
            event_age_groups: (e.age_groups || []).map(ag => {
              if (typeof ag === 'object') {
                return ag.name || ag.friendly_name || `${ag.from || ''}-${ag.to || ''}`;
              }
              return ag;
            }),
            ticket_price: e.cheapest_tickets || null,
            ticket_url: e.ticket_purchase_url || "",
            max_attendance: e.max_attendance || null
          };
        });

      const payload = {
        company_id: companyId,
        selected_events,
        existing_ads
      };

      const resp = await fetch('/facebook_ads/n8n_generate_ads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) throw new Error('Failed to send data to n8n');

      // Set sessionStorage to track generation in progress
      sessionStorage.setItem('metaAdsAI_generating', 'true');
      sessionStorage.setItem('metaAdsAI_generation_start', new Date().toISOString());

      alert(`Sent ${selectedEventIds.length} event(s) to n8n to create Facebook ads.`);
      window.location.href = companySlug ? `/marketing/meta_ads_ai/companies/${companySlug}` : '/marketing/meta_ads_ai';
    } catch (err) {
      console.error(err);
      alert('Error preparing or sending data.');
    }
  };

  return (
    <div className="marketing__content cts-container">
      {(isLoading || isFetching) && <Loader />}
      <h2 className="feature-heading text-capitalize text-center">Facebook Ads Campaign</h2>
      <div className="marketing__content__events justify-content-center">
        {/* Sidebar start */}
        <div className="marketing__content__events__sidebar w-75">
          {/* Top action bar with Generate */}
          <div className="event-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 className="title">Select Events</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btns btn-primarys"
                disabled={selectedEventIds.length === 0}
                onClick={handleGenerate}
                style={selectedEventIds.length === 0 ? { opacity: 0.5, pointerEvents: 'none' } : {}}
              >
                Generate ({selectedEventIds.length})
              </button>
              <button
                type="button"
                className="btns btn-outlines"
                onClick={toggleSelectAll}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>

          {eventsItems.length === 0 && (
            <div className="event-header">
              <h2 className="title text-center">No upcoming events to select.</h2>
            </div>
          )}

          <div className="events_container">
            {eventsItems.map((event) => (
              <div
                className={`events_container__item ${selectedEventIds.includes(event.id) ? 'active' : ''}`}
                key={event.id}
                onClick={() => toggleSelect(event.id)}
                style={{
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <img
                  src={event.banner_image}
                  alt="event"
                  width="76"
                  height="46"
                  style={{ marginRight: 12 }}
                />
                <div>
                  <h4 className="text-ellipse">{event.name}</h4>
                  <span>{event.event_address}</span> | {" "}
                  <span className="ml-1">{event?.start_date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Sidebar end */}
      </div>
    </div>
  );
};

export default EventFacebookAdsCampaignPage;
